import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Typography } from "@mui/material";
import color from "../../../Constants/colors";
import { useDocumentCraftingUsage } from "../../../Query/useDocumentCrafting";
import {
  ErrorState,
  LoadingState,
  Panel,
  RunStatusChip,
} from "../Common/DocumentCraftingUi";
import { formatCost, formatDateTime } from "../Common/format";
import { useDocumentCraftingEnumLabels } from "../Common/useDocumentCraftingEnumLabels";

const SHOWN_RUNS = 5;

/** "AI usage for this document" (owner and managers, SPEC §4.14): the sum
 * over all runs, discarded drafts included, and the latest runs. */
const DocumentUsagePanel: React.FC<{ documentId: number }> = ({ documentId }) => {
  const { t, i18n } = useTranslation("documentCrafting");
  const labels = useDocumentCraftingEnumLabels();
  const usage = useDocumentCraftingUsage(documentId);
  const [showAll, setShowAll] = useState(false);

  const runs = usage.data?.runs ?? [];
  // Newest first, whatever order the server sends.
  const sorted = [...runs].sort((a, b) => b.started_at.localeCompare(a.started_at));
  const shown = showAll ? sorted : sorted.slice(0, SHOWN_RUNS);

  return (
    <Panel title={t("detail.usage.title")} subtitle={t("detail.usage.subtitle")}>
      {usage.isLoading ? (
        <LoadingState />
      ) : usage.isError || !usage.data ? (
        <ErrorState onRetry={() => usage.refetch()} />
      ) : (
        <>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1.5 }}>
            <Typography sx={{ fontSize: 26, fontWeight: 800, color: color.gray900 }}>
              {formatCost(usage.data.total_cost, i18n.language)}
            </Typography>
            <Typography sx={{ fontSize: 13, color: color.gray500 }}>
              {t("detail.usage.runs", { count: runs.length })}
            </Typography>
          </Box>
          {runs.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: color.gray500 }}>
              {t("detail.usage.empty")}
            </Typography>
          ) : (
            <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
              {shown.map((run) => (
                <Box
                  component="li"
                  key={run.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    py: 0.75,
                    borderTop: `1px solid ${color.gray100}`,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: color.gray900 }}>
                      {labels.runKind(run.kind)}
                    </Typography>
                    <Typography noWrap sx={{ fontSize: 12, color: color.gray500 }}>
                      {formatDateTime(run.started_at, i18n.language)}
                      {run.model_name && ` · ${run.model_name}`}
                    </Typography>
                  </Box>
                  <RunStatusChip status={run.status} />
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: color.gray900,
                      minWidth: 64,
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {formatCost(run.cost, i18n.language)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          {runs.length > SHOWN_RUNS && (
            <Button
              size="small"
              color="secondary"
              onClick={() => setShowAll((value) => !value)}
              sx={{ mt: 1 }}
            >
              {showAll
                ? t("common.showLess")
                : t("detail.usage.showAll", { count: runs.length })}
            </Button>
          )}
        </>
      )}
    </Panel>
  );
};

export default DocumentUsagePanel;
